// Local dev-login: the pre-defined identities, so a role can be exercised
// without MPass. Owner ruling of 2026-09-06: this page stays, with its
// predefined roles and users — it is the only way into the system until MPass is
// wired, and its lede no longer claims otherwise.
//
// It is reached from the entry screen, as the action beneath the MPass button,
// and its route is declared unconditionally (spec 036 FR-04/FR-05). The lede
// used to say the page was live "wherever VITE_ENABLE_DEV_LOGIN is on"; there is
// no such variable in the path any more, so it says what is true instead.
//
// Drawn to `wireframes/react/src/pages/Rol.tsx` on the owner's instruction of
// 2026-09-09 ("implement the right design from the wireframes /rol, make it
// pixel perfect"). It was a bare list of twelve rows, each with its own "Intră"
// — the exact shape the 2026-08-30 audit had already struck off the sibling
// screen (§4 row 1, quoted in `RoleSelectPage.tsx:4-6`): a radio-card group and
// ONE full-width continue, not a button per row. The card, its 480px measure and
// the option rows are `RoleSelectPage`'s to the pixel, and deliberately so —
// the two screens draw the same picture, so they share the rules rather than
// each keeping a copy that can drift. Two callers is not three, so nothing is
// abstracted (R4): the stylesheet simply names both.
//
// What differs is what is being chosen. `/rol` picks a role for one known
// person, so its card head is that person; this picks WHICH person, so the head
// is the page's own title and the identity rides in each row.
//
// The identities themselves are NOT declared here. They live in
// `@simsm/shared-types` (`lib/dev-identities.ts`), because this page and
// `apps/frontoffice-e2e/tests/_helpers/mpass-fixtures.ts` used to keep separate
// lists that disagreed on every entry and, twice, on the ROLE an IDNP carries.
// This file owns only what a list needs and a session does not: what to call
// each row, and which organisation to name after the person.
import { useCallback, useState } from 'react';
import type { KeyboardEvent as ReactKeyboardEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslate } from '@tolgee/react';
import {
  MudButton,
  MudIcon,
  MudRadio,
  MudRadioGroup,
  MudTag,
  MudTooltip,
} from '@egov-moldova/mud-react';
import {
  DEV_IDENTITIES,
  devLoginBodyFor,
  type DevIdentity,
  type DevIdentityKey,
  type SessionUser,
} from '@simsm/shared-types';
import { apiClient, isNormalizedApiError } from '../app/api-client';
import { landingPathFor } from '../app/role-home';
// The drawn option carries a scope tag (`Rol.tsx:26`). The role codes behind it
// may not appear in this file — see `role-reach.ts` and D-15's guard.
import { scopeKeyFor } from '../app/role-reach';
import { markSessionEstablished } from '../app/session-presence';
import { clearActiveRegistrar } from '../app/active-registrar';

// A row is a ROLE, not a person: main text the role, sub-text the institution
// it acts for, and the tag its reach (owner instruction 2026-09-09). Romanian,
// like the rest of this dev-only screen, and keyed by identity so a row cannot
// be written for an identity that does not exist.
//
// The roles are ATOMIC. The caiet lets a USER hold several — "Utilizatorul
// autentificat poate avea mai multe roluri în sesiune" (UC10-04), which is the
// whole reason UC12 exists — but a role itself is one thing, and a row reading
// "Operator + Administrator (multi-rol)" named a role that does not exist. The
// two fused rows are gone; the atomic roles they combined are already in the
// list on their own.
//
// Their identities stay in the registry and are NOT removed: `operator-multi-role`
// is what `role-select.spec.ts` and `routing.spec.ts` authenticate as, and e2e
// posts to `/auth/dev-login` directly rather than clicking this page, so the
// registry and this list can differ. `PAGE_KEYS` is that difference, and the
// only thing this file decides about the registry.
const ROWS: Record<DevIdentityKey, { role: string; institution: string }> = {
  'operator-single-location': {
    role: 'Operator Registrator',
    institution: '[SEED] Farmacia Seed Unu',
  },
  'operator-two-locations': {
    role: 'Operator Registrator',
    institution: '[SEED] Farmacia Seed Unu · două locații',
  },
  'operator-imsp': {
    role: 'Operator Registrator IMSP',
    institution: '[SEED] Spitalul Seed IMSP',
  },
  'operator-non-imsp': {
    role: 'Operator Registrator',
    institution: '[SEED] Farmacia Seed Non-IMSP',
  },
  // Two roles in one session. The ROLE stays atomic — the caiet has no role
  // called "Operator + Administrator" — but the session carries both, which is
  // the only way to exercise UC12's switch by hand. What it holds is said on the
  // institution line, where a qualifier belongs, never in the role's name.
  'operator-multi-role': {
    role: 'Operator Registrator',
    institution: '[SEED] Farmacia Seed Unu · și Administrator Registrator',
  },
  'registrar-admin': {
    role: 'Administrator Registrator',
    institution: '[SEED] Farmacia Seed Unu',
  },
  'registrar-admin-two-registrars': {
    role: 'Administrator Registrator',
    institution: '[SEED] Farmacia Seed Unu + [SEED] Depozitul Seed Doi',
  },
  'registrar-admin-imsp': {
    role: 'Administrator Registrator IMSP',
    institution: '[SEED] Institutul Seed IMSP',
  },
  'system-admin': { role: 'Administrator Sistem', institution: 'AMDM' },
  'system-admin-observer': {
    role: 'Administrator Sistem',
    institution: 'AMDM · și Observator',
  },
  'observer-general': { role: 'Observator', institution: 'Observator general' },
  'observer-ms': { role: 'Observator', institution: 'Ministerul Sănătății' },
  'observer-cnam': { role: 'Observator', institution: 'CNAM' },
  'observer-ansp': { role: 'Observator', institution: 'ANSP' },
  // No institution, because there is no role to act for one with — this is the
  // identity UC10-06 means to refuse. A spec reference is not an institution and
  // has no business sitting where every other row names one.
  'no-role': { role: 'Rol nedefinit în SIMSM', institution: '—' },
};

// The rows this page offers, in order. Every row names ONE role; two of the
// identities behind them carry a second one, which their institution line says
// and which is what makes UC12's switch reachable by hand.
const PAGE_KEYS: readonly DevIdentityKey[] = [
  'operator-single-location',
  'operator-two-locations',
  'operator-imsp',
  'operator-non-imsp',
  'operator-multi-role',
  'registrar-admin',
  'registrar-admin-two-registrars',
  'registrar-admin-imsp',
  'system-admin',
  'system-admin-observer',
  'observer-general',
  'observer-ms',
  'observer-cnam',
  'observer-ansp',
  'no-role',
];

const PAGE_IDENTITIES: readonly DevIdentity[] = PAGE_KEYS.map((key) => DEV_IDENTITIES[key]);

// The drawn sub-line is the ORGANISATION and nothing else (`Rol.tsx:25`,
// `<small>{v.org}</small>`) — one line, never wrapped, ellipsised when the name
// is long. The tooltip on the role above repeats it in full, which is the only
// reason that tooltip exists.
//
// The person and the IDNP are rendered nowhere (owner instruction 2026-09-09).
// They were registry bookkeeping: a reader picking a role has no use for either,
// and joining them onto the sub-line with dots made one run of text that read as
// a sentence and truncated mid-word. They remain in `DEV_IDENTITIES`, which is
// what the login below actually posts.

export function DevLoginPage(): JSX.Element {
  const { t } = useTranslate();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  // The first identity is the pre-selection, so Continue is never a dead
  // control and the card opens on something rather than nothing. The registry
  // is a non-empty literal, but it is typed as an array, so the fallback is
  // written rather than asserted away.
  const [picked, setPicked] = useState<DevIdentityKey | null>(PAGE_IDENTITIES[0]?.key ?? null);
  const [busy, setBusy] = useState(false);

  // `mud-radio-group` (MUD 1.2) owns the radiogroup keyboard contract: one Tab
  // stop, Arrow keys move the selection and focus (wrapping), Space selects. It
  // finds the radios as descendants, so the card wrappers stay as they are. It
  // has no Home/End, which this page has always answered, so those two keys are
  // the only handling left here. Copied from `RoleSelectPage`: two callers is
  // not three (R4).
  const handleKeyDown = useCallback((event: ReactKeyboardEvent<HTMLElement>) => {
    if (event.key !== 'Home' && event.key !== 'End') return;
    const radios = Array.from(event.currentTarget.querySelectorAll('mud-radio')).filter(
      (radio) => !radio.disabled,
    );
    const target = event.key === 'Home' ? radios[0] : radios[radios.length - 1];
    const identity = PAGE_IDENTITIES.find((candidate) => candidate.key === target?.value);
    if (!target || !identity) return;
    event.preventDefault();
    setPicked(identity.key);
    target.focus();
  }, []);

  async function loginAs(identity: DevIdentity): Promise<void> {
    let response;
    try {
      // The persona's MPass codes, resolved the way SAML resolves them: an
      // operator is narrowed to its locations rather than handed every site of
      // the Registrar (docs/shape-ops-dev-identities.md §1).
      response = await apiClient.post<SessionUser>('/auth/dev-login', devLoginBodyFor(identity));
    } catch (error) {
      // The development login answers to the same gate MPass login does, so it
      // refuses an identity whose role SIMSM does not define with the same
      // ROLE_NOT_DEFINED the AssertionConsumerService raises. Nothing caught it
      // before: the click simply did nothing and the console carried the only
      // trace (audit-system-admin-2026-08-30 S5). It now lands on UC10-06's
      // screen, which is the whole point of the refusal.
      if (isNormalizedApiError(error) && error.code === 'ROLE_NOT_DEFINED') {
        navigate('/auth/no-role', { replace: true });
        return;
      }
      throw error;
    }
    queryClient.setQueryData(['session'], response.data);
    markSessionEstablished();
    // A new sign-in chooses its Registrar again, as an MPass one does
    // (docs/shape-ops-tenant-selector.md §7.2).
    clearActiveRegistrar();
    navigate(landingPathFor(identity.activeRole, [...identity.availableRoles]), { replace: true });
  }

  return (
    <main className="simsm-dev-login">
      <div className="simsm-dev-login__card">
        {/* The drawn card head: centred, over a hairline (`Rol.tsx:17`). On
            `/rol` it is the signed-in person; here it is the page itself,
            because the person is what the rows below are choosing. */}
        <header className="simsm-dev-login__header">
          <p className="simsm-dev-login__eyebrow">{t('dev_login.eyebrow')}</p>
          <h1 className="simsm-dev-login__title">{t('dev_login.title')}</h1>
          <p className="simsm-dev-login__lede">{t('dev_login.lede')}</p>
        </header>

        <div className="simsm-dev-login__body">
          <h2 className="simsm-dev-login__group-label">{t('role_select.group.label')}</h2>

          {/* The heading above is the visible label; the group carries the same
              text as its accessible name (MUD moves `aria-label` onto its
              internal radiogroup). */}
          <MudRadioGroup
            aria-label={t('role_select.group.label')}
            className="simsm-dev-login__list"
            {...(picked ? { value: picked } : {})}
            onMudChange={(event: CustomEvent<{ value: string | undefined }>) => {
              const identity = PAGE_IDENTITIES.find(
                (candidate) => candidate.key === event.detail.value,
              );
              if (identity) setPicked(identity.key);
            }}
            onKeyDown={handleKeyDown}
          >
            {PAGE_IDENTITIES.map((identity) => {
              const isSelected = identity.key === picked;
              const { role, institution } = ROWS[identity.key];
              return (
                <div
                  key={identity.idnp}
                  className={`simsm-dev-login__option${isSelected ? ' is-selected' : ''}`}
                  // The whole card selects, which is how MUD's own
                  // `.role-switch-item` behaves. It is also load-bearing here:
                  // the tooltip's hit area covers the text block, so a click on
                  // either line lands on that overlay and never reaches the
                  // radio. Keyboard is untouched — the radio is still the
                  // control, still focusable, still arrow-navigable.
                  onClick={() => setPicked(identity.key)}
                >
                  <MudRadio
                    value={identity.key}
                    // The tag is drawn at the row's end, as the drawing places
                    // it, so it is no longer inside the radio's slotted label.
                    // The reach still has to be announced with the role, so it
                    // rides in the accessible name instead of being lost.
                    aria-label={`${role}, ${institution}, ${t(scopeKeyFor(identity.activeRole))}`}
                  >
                    {/* MUD's tooltip carries what the row cannot show: the
                        person and the IDNP behind this role. It hangs off the
                        role name, NOT the sub-line — `mud-tooltip` exposes no
                        parts, and its inner `.trigger` is an `inline-flex` box
                        that does not clamp to the host, so a truncating line
                        placed inside it overflows and is cut mid-word with no
                        ellipsis. The name never truncates, so it can be a
                        trigger; the sub-line cannot. */}
                    <span slot="label" className="simsm-dev-login__option-text">
                      <span className="simsm-dev-login__option-name">{role}</span>
                      <span className="simsm-dev-login__option-meta">{institution}</span>
                      {/* The trigger is an empty hit area laid over both lines,
                          not the lines themselves. `mud-tooltip` exposes no
                          parts, and its inner `.trigger` is an inline-flex box
                          that does not shrink to its host — measured 315px
                          inside a 253px host — so text placed inside it
                          overflows instead of ellipsising. An empty trigger has
                          no width to fight over, and the text keeps its own box.
                          Clicks still reach the radio: this sits inside
                          `mud-radio`'s label, and a click on a label's content
                          activates its control.

                          The bubble carries the role over the institution —
                          nothing else. The person and the IDNP were registry
                          bookkeeping (owner instruction 2026-09-09); what this
                          is FOR is the institution, whose sub-line ellipsises. */}
                      <MudTooltip
                        className="simsm-dev-login__option-tip"
                        position="top"
                        size="sm"
                        /* AGE's own default is 200ms, which reads as instant and
                           made the bubble flash up while the pointer was only
                           crossing the list. 500 is the pause a reader has to
                           mean; `hideDelay` stays AGE's 150 so leaving still
                           feels immediate. */
                        showDelay={500}
                      >
                        <span slot="trigger" className="simsm-dev-login__option-hit" />
                        <span slot="title">{role}</span>
                        <span slot="description">{institution}</span>
                      </MudTooltip>
                    </span>
                  </MudRadio>
                  {/* `.role-opt` ends with the tag, vertically centred against
                      the whole row rather than sitting on the name's line
                      (`Rol.tsx:26`; measured 17px from the row's end edge). */}
                  <MudTag
                    className="simsm-dev-login__option-tag"
                    variant="info"
                    size="sm"
                    type="subtle"
                    semantic="neutral"
                  >
                    {t(scopeKeyFor(identity.activeRole))}
                  </MudTag>
                  {/* MUD's selection indicator, always present so the row does
                      not reflow when the choice moves. */}
                  <span
                    className="simsm-dev-login__option-check"
                    data-checked={isSelected ? 'true' : 'false'}
                    aria-hidden="true"
                  >
                    <MudIcon name="checkmark-small" size={20} />
                  </span>
                </div>
              );
            })}
          </MudRadioGroup>

          <MudButton
            className="simsm-dev-login__continue"
            shape="circular"
            variant="primary"
            size="lg"
            fullWidth
            disabled={busy}
            onClick={() => {
              const identity = PAGE_IDENTITIES.find((candidate) => candidate.key === picked);
              if (!identity) return;
              setBusy(true);
              void loginAs(identity).finally(() => setBusy(false));
            }}
          >
            {t('role_select.action.continue')}
            <MudIcon slot="icon-end" name="arrow-right" size={20} />
          </MudButton>

          {/* Owner instruction 2026-09-09: the way back stays, but as a button.
              Outlined, not filled — the drawing's rule is that one action per
              screen is saturated, and on this screen that is Continue. It routes
              rather than following an href, so a navigation is not a page load
              and the session query cache survives it. */}
          <div className="simsm-dev-login__footer">
            <MudButton
              className="simsm-dev-login__back"
              shape="circular"
              variant="strict"
              appearance="outlined"
              size="lg"
              fullWidth
              onClick={() => navigate('/')}
            >
              <MudIcon slot="icon-start" name="arrow-left" size={20} />
              {t('dev_login.back')}
            </MudButton>
          </div>
        </div>
      </div>
    </main>
  );
}
